import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Compass, HeartHandshake, Sparkles, MountainSnow, Users, ShieldCheck, Rocket } from "lucide-react";
import Shell from "@/components/Shell";

const FOUNDER_IMG = "/founder.jpg";

const fade = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }) };

const VALUES = [
  { icon: MountainSnow, t: "Uncover hidden India", d: "We put offbeat places, local guides and untold trails on the map — beyond the crowded checklist." },
  { icon: Users, t: "Betterment of people", d: "Every booking channels income to local hosts, guides and small operators, lifting the communities you visit." },
  { icon: Sparkles, t: "Technology for travel", d: "AI planning, instant booking and smart discovery make great journeys effortless for every Indian." },
  { icon: ShieldCheck, t: "Trust & safety first", d: "Verified experiences, secure payments and honest pricing — travel with confidence." },
];

export default function About() {
  return (
    <Shell>
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-10">
        <motion.span initial="hidden" animate="show" variants={fade} className="inline-flex items-center gap-2 rounded-full border border-primary/15 bg-secondary/60 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-foreground/70">
          <Compass size={14} className="text-accent" /> Our story
        </motion.span>
        <motion.h1 custom={1} initial="hidden" animate="show" variants={fade} className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mt-6 text-foreground max-w-3xl">
          Zatayoo — <span className="italic text-accent">Dare To Rise.</span>
        </motion.h1>
        <motion.p custom={2} initial="hidden" animate="show" variants={fade} className="mt-5 text-xl text-muted-foreground max-w-2xl">
          Bringing technology to travel. A homegrown Indian initiative on a mission to change the way the country explores — forever.
        </motion.p>
      </section>

      {/* VISION */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="rounded-[2rem] bg-primary text-primary-foreground p-10 md:p-16">
          <span className="text-xs font-semibold uppercase tracking-widest text-accent">The vision</span>
          <h2 className="font-display text-3xl sm:text-4xl mt-3 leading-tight max-w-3xl">
            To make the real, hidden, soulful India reachable for everyone — while lifting the people and places that make it magic.
          </h2>
          <p className="mt-5 text-primary-foreground/75 text-lg max-w-2xl leading-relaxed">
            Zatayoo was born from a simple belief: travel should do more than move you from one city to another. It should open minds,
            support local livelihoods, and celebrate the incredible diversity of Bharat. We blend cutting-edge technology — AI trip planning,
            instant flights, hotels, buses and cabs — with a deep love for the land, so every journey is easier, richer and more meaningful.
          </p>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="md:col-span-5">
            <div className="relative">
              <img src={FOUNDER_IMG} alt="Mr. Prakash Singh, Founder of Zatayoo" className="w-full h-[440px] object-cover rounded-[2rem] border border-primary/10" />
              <div className="absolute -bottom-5 left-5 right-5 bg-card border border-primary/10 rounded-2xl p-4 shadow-sm">
                <p className="font-display text-xl text-foreground">Mr. Prakash Singh</p>
                <p className="text-sm text-accent font-semibold">Founder & CEO · "Yatra King"</p>
              </div>
            </div>
          </motion.div>
          <motion.div custom={1} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="md:col-span-7">
            <span className="text-xs font-semibold uppercase tracking-widest text-accent">Meet the founder</span>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-foreground mt-3">The Yatra King with a bold dream</h2>
            <p className="mt-5 text-lg text-foreground/80 leading-relaxed">
              Zatayoo is the brainchild of <b>Mr. Prakash Singh</b> — a young, fiercely patriotic entrepreneur who believes India's greatest
              stories are still waiting to be discovered. A traveller at heart and a technologist by craft, Prakash set out to build a
              proudly Indian platform that could stand shoulder-to-shoulder with the world's best.
            </p>
            <p className="mt-4 text-lg text-foreground/80 leading-relaxed">
              His conviction is simple: when technology meets the spirit of exploration, ordinary trips become life-changing journeys — and
              the communities we visit rise along with us. Under his leadership, Zatayoo is more than a booking app; it's a movement to put
              hidden India on the global map and to empower local guides, hosts and small businesses across the country.
            </p>
            <blockquote className="mt-6 border-l-4 border-accent pl-5 text-xl font-display italic text-foreground">
              "We don't just help you travel. We dare you to rise — to see your own country with new eyes, and to leave every place better than you found it."
            </blockquote>
            <p className="mt-3 text-sm text-muted-foreground">— Prakash Singh, Founder</p>
          </motion.div>
        </div>
      </section>

      {/* VALUES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-foreground">What we stand for</h2>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v, i) => {
            const Icon = v.icon;
            return (
              <motion.div key={v.t} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="rounded-2xl border border-primary/10 bg-card p-7">
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-accent/10 text-accent"><Icon size={22} /></span>
                <h3 className="font-semibold text-lg mt-4 text-foreground">{v.t}</h3>
                <p className="text-muted-foreground mt-2 leading-relaxed text-sm">{v.d}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <div className="rounded-[2rem] border border-primary/10 bg-secondary/40 p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-foreground flex items-center gap-3"><Rocket className="text-accent" /> Join the rise.</h2>
            <p className="text-muted-foreground mt-2 text-lg">Start your next journey with Zatayoo — the smart, soulful way to explore India.</p>
          </div>
          <Link to="/explore" className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-7 py-4 font-semibold hover:-translate-y-px transition-transform whitespace-nowrap" data-testid="about-cta">
            Explore experiences <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </Shell>
  );
}
